import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import ImportanceBadge from "./ImportanceBadge";
import { intelApi, fmtDate } from "../../lib/intelApi";
import { SkeletonCards, ErrorState, EmptyState } from "./IntelStates";
import { TECHS } from "./IntelFilters";

const GROUPS = [
  { key: "month", label: "Month" },
  { key: "technology", label: "Technology" },
  { key: "importance", label: "Importance" },
];

const iso = (d) => d.toISOString().slice(0, 10);

export default function TimelineView() {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 90);
  const [range, setRange] = useState({ from: iso(start), to: iso(today) });
  const [groupBy, setGroupBy] = useState("month");
  const [tech, setTech] = useState("All");

  const query = useQuery({
    queryKey: ["intel-timeline", range, groupBy, tech],
    queryFn: () => intelApi.timeline({ date_from: range.from, date_to: range.to, group_by: groupBy, technology: tech }),
    enabled: !!range.from && !!range.to,
  });

  return (
    <section data-testid="timeline-view" className="space-y-8">
      <div className="border border-border bg-surface/40 p-5 md:p-6 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground w-24 shrink-0">Range</div>
          <input
            type="date"
            data-testid="timeline-from"
            value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="bg-transparent border-b border-border focus:border-accent focus:outline-none py-2 font-mono text-sm"
          />
          <span className="font-mono text-xs text-muted-foreground">→</span>
          <input
            type="date"
            data-testid="timeline-to"
            value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="bg-transparent border-b border-border focus:border-accent focus:outline-none py-2 font-mono text-sm"
          />
        </div>
        <div className="lg:col-span-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground w-24 shrink-0">Group by</div>
          <div className="flex gap-1.5">
            {GROUPS.map((g) => (
              <button
                key={g.key}
                type="button"
                data-testid={`timeline-group-${g.key}`}
                onClick={() => setGroupBy(g.key)}
                className={`font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${
                  groupBy === g.key ? "border-accent bg-accent text-accent-foreground" : "border-border hover:border-accent hover:text-accent"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground w-24 shrink-0">Technology</div>
          <select
            data-testid="timeline-tech"
            value={tech}
            onChange={(e) => setTech(e.target.value)}
            className="bg-background border border-border focus:border-accent focus:outline-none px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em]"
          >
            {TECHS.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {query.isLoading && <SkeletonCards count={2} />}
      {query.isError && <ErrorState onRetry={query.refetch} />}
      {query.data && query.data.total === 0 && (
        <EmptyState title="No updates in this range" body="Try widening the date range or clearing the technology filter." />
      )}
      {query.data && query.data.total > 0 && (
        <div className="space-y-12">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="timeline-total">
            {query.data.total} updates · {fmtDate(range.from)} → {fmtDate(range.to)}
          </div>
          {query.data.groups.map((g) => (
            <div key={g.key} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10" data-testid={`timeline-group-${g.key}`}>
              <div className="md:col-span-3 md:sticky md:top-28 self-start">
                <div className="font-heading font-black text-2xl md:text-3xl tracking-tighter uppercase leading-none">{g.label}</div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">{g.count} updates</div>
              </div>
              <ol className="md:col-span-9 border-l border-border">
                {g.items.map((item, i) => (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.03 }}
                    className="relative pl-6 md:pl-8 pb-6 group"
                  >
                    <span className="absolute -left-[5px] top-2 w-[9px] h-[9px] bg-background border border-accent" />
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <span className="font-mono text-[11px] tracking-[0.1em] text-muted-foreground w-24">{fmtDate(item.published_at)}</span>
                      <ImportanceBadge level={item.importance} />
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {item.technology} · {item.category}
                      </span>
                    </div>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-start gap-1.5 font-heading font-bold text-base md:text-lg tracking-tight leading-snug group-hover:text-accent transition-colors"
                    >
                      {item.title}
                      <ArrowUpRight size={14} className="mt-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                    {item.ai_summary && <p className="mt-1.5 text-sm text-foreground/70 leading-relaxed max-w-[70ch]">{item.ai_summary}</p>}
                  </motion.li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
